import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { API_ENDPOINTS, ApiResponse } from "@/constants/api";
import { fontFamily } from "@/styles/font";
import { logSearchClick } from "@/api/alcoholApi";
import logger from "@/utils/logger";

// Styled Components
const Container = styled.div`
    position: relative;
    width: 100%;
    max-width: 393px;
    height: 100vh; /* 전체 화면 높이로 설정 */
    background: #ffffff;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto; /* 세로 스크롤 활성화 */
`;

const SearchBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px 8px 8px 4px;
    gap: 4px;
    width: 100%;
    height: 56px;
    background: #ffffff;
`;

const BackButton = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

const BackIcon = styled.img`
    width: 24px;
    height: 24px;
`;

const SearchField = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px;
    gap: 4px;
    width: 100%;
    height: 40px;
    background: #f9f9f9;
    border-radius: 12px;
`;

const SearchIcon = styled.img`
    width: 24px;
    height: 24px;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 24px;
    font-family: "Pretendard", sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #17191a;
    background: transparent;
    border: none;
    outline: none;
    flex: 1;

    &::placeholder {
        color: #a4adb2;
    }
`;

interface ClearButtonProps {
    visible: boolean;
}

const ClearButton = styled.div<ClearButtonProps>`
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: ${(props) => (props.visible ? "block" : "none")};
`;

const ClearIcon = styled.img`
    width: 24px;
    height: 24px;
`;

const ResultsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    width: 100%;
    box-sizing: border-box;
    padding-bottom: 80px;
    margin-bottom: 16px;
`;

interface ResultItemProps {
    index: number;
    isSelected?: boolean;
}

const ResultItem = styled.div<ResultItemProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 16px;
    gap: 16px;
    width: 100%;
    height: 80px;
    background: ${(props) => {
        if (props.isSelected) return "#F9F9F9";
        return "#FFFFFF";
    }};
    box-sizing: border-box;
    margin-bottom: 1px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background: #f9f9f9;
    }
`;

const ResultInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    gap: 4px;
    flex: 1;
    min-width: 0;
`;

const ResultTitle = styled.h3`
    width: 100%;
    height: 24px;
    font-family: "Pretendard", sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: #17191a;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 250px;

    @media screen and (max-width: 480px) {
        font-size: 16px;
        max-width: 200px;
    }
`;

const ResultSubtitle = styled.p`
    width: 100%;
    height: 20px;
    font-family: "Pretendard", sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #464a4d;
    margin: 4px 0 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;

    @media screen and (max-width: 480px) {
        font-size: 12px;
        max-width: 130px;
    }
`;

const AlcoholTypeTag = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 1px 5px;
    gap: 10px;
    width: auto;
    min-width: 57px;
    height: 20px;
    border: 1px solid #ffc700;
    border-radius: 10px;
`;

const AlcoholTypeText = styled.div`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    line-height: 17px;
    display: flex;
    align-items: center;
    text-align: center;
    color: #ffc700;
    white-space: nowrap;
`;

const EmptyResultsMessage = styled.div`
    width: 100%;
    padding: 40px 0;
    text-align: center;
    font-family: "Pretendard", sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #a4adb2;
`;

// 검색 결과 인터페이스 정의
interface SearchResult {
    altKey: string;
    name: string;
    breweryAltKey: string;
    breweryName: string;
    type: string;
    abv: number;
}

interface LocationState {
    searchTerm?: string;
}

const Search: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get("keyword") || "");
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // 페이지 로드 시 자동으로 입력 필드에 포커스
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    // 검색어가 변경될 때마다 URL 업데이트
    useEffect(() => {
        if (searchTerm) {
            navigate(`/search?keyword=${encodeURIComponent(searchTerm)}`, { replace: true });
        } else {
            navigate("/search", { replace: true });
        }
    }, [searchTerm, navigate]);

    // 검색어가 변경될 때마다 API 호출
    useEffect(() => {
        const fetchSearchResults = async () => {
            if (searchTerm.trim() === "") {
                setSearchResults([]);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const response = await api.get<ApiResponse<SearchResult[]>>(
                    `${API_ENDPOINTS.ALCOHOL_SEARCH}?name=${encodeURIComponent(searchTerm)}`
                );

                if (response.data.success) {
                    setSearchResults(response.data.data);
                } else {
                    setError(response.data.message || "검색 중 오류가 발생했습니다.");
                    setSearchResults([]);
                }
            } catch (err) {
                logger.error("검색 API 호출 중 오류:", err);
                setError("검색 중 오류가 발생했습니다. 다시 시도해주세요.");
                setSearchResults([]);
            } finally {
                setIsLoading(false);
            }
        };

        // 디바운스 처리
        const debounceTimer = setTimeout(() => {
            if (searchTerm.trim() !== "") {
                fetchSearchResults();
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleClearClick = () => {
        setSearchTerm("");
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setSelectedItemId(null); // 검색어 변경 시 선택 초기화
    };

    const handleResultItemClick = (altKey: string) => {
        setSelectedItemId(altKey);

        // 검색 로그 저장
        if (searchTerm.trim()) {
            logSearchClick(altKey, searchTerm.trim()).catch((err) => logger.error("검색 로그 저장 실패:", err));
        }

        // 상세 페이지로 이동
        navigate(`/alcohol/${altKey}?search=${encodeURIComponent(searchTerm)}`);
    };

    return (
        <Container>
            <SearchBarContainer>
                <BackButton onClick={handleBackClick}>
                    <BackIcon src="/icon/chevron-left.png" alt="뒤로가기" />
                </BackButton>
                <SearchField>
                    <SearchIcon src="/icon/search.png" alt="검색" />
                    <SearchInput
                        ref={inputRef}
                        value={searchTerm}
                        onChange={handleInputChange}
                        placeholder="검색어를 입력해주세요"
                    />
                    <ClearButton visible={searchTerm.length > 0} onClick={handleClearClick}>
                        <ClearIcon src="/icon/delete-filled.png" alt="지우기" />
                    </ClearButton>
                </SearchField>
            </SearchBarContainer>

            {searchTerm.trim() !== "" && (
                <ResultsContainer>
                    {isLoading ? (
                        <EmptyResultsMessage>검색 중...</EmptyResultsMessage>
                    ) : error ? (
                        <EmptyResultsMessage>{error}</EmptyResultsMessage>
                    ) : searchResults.length > 0 ? (
                        searchResults.map((result) => (
                            <ResultItem
                                key={result.altKey}
                                index={parseInt(result.altKey) || 0}
                                isSelected={selectedItemId === result.altKey}
                                onClick={() => handleResultItemClick(result.altKey)}
                            >
                                <ResultInfo>
                                    <ResultTitle>{result.name}</ResultTitle>
                                    <ResultSubtitle>{result.breweryName}</ResultSubtitle>
                                </ResultInfo>
                                <AlcoholTypeTag>
                                    <AlcoholTypeText>{result.type}</AlcoholTypeText>
                                </AlcoholTypeTag>
                            </ResultItem>
                        ))
                    ) : (
                        <EmptyResultsMessage>검색 결과가 없습니다.</EmptyResultsMessage>
                    )}
                </ResultsContainer>
            )}
        </Container>
    );
};

export default Search;
